import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo } from 'react'
import { Formik } from 'formik'
import { makeStyles, shorthands, tokens } from '@fluentui/react-components'
import { LegalAidCertificateProps } from '../../../pages/File/Registration/LegalAidCertificateOverview'
import Loading from '../../../components/Loading'
import { formatIsoDate } from '../../../helpers/dateHelper'
import { Alert } from '@fluentui/react-components/unstable'
import { IFileDto } from '../../../config/fileConfig'
import useAxios from '../../../hooks/useAxios'
import { getId } from '@fluentui/react'
import FormContainer from './FormContainer'
import { CreateDto, FilePartyAction, FilePartyRole, MainDto } from '../../../config/filePartyConfig'
import { AxiosResponse } from 'axios'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import { useDispatch } from 'react-redux'

import * as Yup from 'yup'
import { Currency } from '../../../config/i18nConfig'
import { useTranslation } from 'react-i18next'

type BaseLegalAidCertificate = {
    applied: string
    issued: string
    reference: string
    baseHours: string
    travelHours: string
    travelDistance: string
}

export type LegalAidCertificate = BaseLegalAidCertificate & {
    id: string
    file: {
        id: string
    }
    baseFee: string
    travelFee: string
    travelExpenses: string
    fixedCompensation: string
    contribution: string
    user: {
        id: string
    }
    contributionInvoice: {
        id: string
        number: string
        pdfId: string
    }
    settlementInvoice: {
        id: string
        number: string
        pdfId: string
    }
    clientPivot: {
        id: string
    }
    legalAidBoardPivot: {
        id: string
    }
    currency: Currency
}

export type LegalAidCertificateDto = BaseLegalAidCertificate & {
    fileId: string
    userId: string
    contributionInvoiceId: string
    settlementInvoiceId: string
    clientPivotId: string
    legalAidBoardPivotId: string
    baseFee: string
    travelFee: string
    travelExpenses: string
    contribution: string
    fixedCompensation: string
    currency: string
}

const useFormStyles = makeStyles({
    alert: {
        ...shorthands.margin(0, tokens.spacingHorizontalS),
    },
})

export type LegalAidCertificateSettings = {
    baseFeeHour: string
    travelFeeHour: string
    travelKm: string
    fixedCompensation: string
    currency: string
    vatType: string
    contributionInvoice: {
        templateId: string // The invoice template.
        feeTemplateId: string // The group template to add the own contribution fee to.
    }
    settlementInvoice: {
        templateId: string // The invoice template.
        feeTemplateId: string // The group template to add the base fee to.
        travelTemplateId: string // The group template to add the travel distance compensation to.
        contributionTemplateId: string // The group template to add the own contribution deposit invoice to.
    }
    legalAidBoardPartyId: string
    legalAidBoardRole: string
    clientRole: string
}

const getPivotIdByRole = (parties: MainDto[], role: string): string | undefined => {
    const matches = parties.filter(party => party.role === role)
    if (matches.length === 1) {
        return matches[0].pivotId
    }
    return undefined
}

const mapCertificate = (
    certificate: LegalAidCertificate,
    settings: LegalAidCertificateSettings,
    file: IFileDto,
    parties: MainDto[],
): LegalAidCertificateDto => ({
    // Master data
    clientPivotId: certificate?.clientPivot?.id || getPivotIdByRole(parties, settings.clientRole || FilePartyRole.Client),
    legalAidBoardPivotId: certificate?.legalAidBoardPivot?.id,
    applied: certificate?.applied || formatIsoDate(new Date()),
    issued: certificate?.issued,
    reference: certificate?.reference,

    // Compensation calculation
    baseHours: certificate?.baseHours || '0.00',
    baseFee: certificate?.baseFee,
    travelHours: certificate?.travelHours || '0.00',
    travelFee: certificate?.travelFee,
    travelDistance: certificate?.travelDistance || '0',
    travelExpenses: certificate?.travelExpenses || '0.00',
    contribution: certificate?.contribution || '0.00',
    fixedCompensation: certificate?.fixedCompensation || settings.fixedCompensation,

    // Invoices
    contributionInvoiceId: certificate?.contributionInvoice?.id,
    settlementInvoiceId: certificate?.settlementInvoice?.id,

    // Hidden fields
    fileId: certificate?.file?.id || file?.id,
    userId: certificate?.user?.id || file?.owner.id,
    currency: certificate?.currency?.alpha || settings.currency || 'EUR',
})

const requiresIssued =
    (message?: string) =>
    ([issued], schema) =>
        issued?.length > 0 ? schema.required(message) : schema.nullable()

// An empty own contribution equals no own contribution (e.g. criminal law), so it must not block validation.
const emptyAsZero = (value: number, original: unknown) => (original === '' || original === null || original === undefined ? 0 : value)

export const getLegalAidCertificateValidationSchema = (translate: (key: string) => string) =>
    Yup.object().shape({
        clientPivotId: Yup.string().uuid().required(translate('legalaidcertificate.client.required')),
        legalAidBoardPivotId: Yup.string().uuid(), // Validation for legal aid board ID is a custom procedure in the submit method.
        applied: Yup.string().required(),
        issued: Yup.string().nullable(),
        reference: Yup.string()
            .nullable()
            .when('issued', requiresIssued(translate('legalaidcertificate.reference.required'))),

        // Compensation calculation
        baseHours: Yup.number().min(0).when('issued', requiresIssued()),
        baseFee: Yup.number().min(0).when('issued', requiresIssued()),
        travelHours: Yup.number().min(0).when('issued', requiresIssued()),
        travelFee: Yup.number().min(0).when('issued', requiresIssued()),
        travelDistance: Yup.number().min(0).when('issued', requiresIssued()),
        travelExpenses: Yup.number().min(0).when('issued', requiresIssued()),
        contribution: Yup.number().transform(emptyAsZero).min(0).when('issued', requiresIssued()),
        fixedCompensation: Yup.number().min(0).when('issued', requiresIssued()),

        // Invoices
        contributionInvoiceId: Yup.string().uuid(),
        settlementInvoiceId: Yup.string().uuid(),

        // Hidden fields
        fileId: Yup.string().uuid().required(),
        userId: Yup.string().uuid().required(),
        currency: Yup.string().length(3).required(), // Alpha-3 currency code.
    })

export type PersistedCertificate = {
    certificateId: string
    legalAidBoardPivotId: string
}

export type SaveCertificate = (
    dto: LegalAidCertificateDto,
    setFieldError: (field: string, message: string) => void,
    options?: {
        // Target certificate for follow-up saves within an async flow, when props.selected is not updated yet.
        certificateId?: string
        // Select the stored certificate and refresh the lists afterwards. Deferring this keeps the Formik
        // subtree (keyed on the selection and certificates list) mounted during a multi-step flow.
        finalize?: boolean
    },
) => Promise<PersistedCertificate | null>

export function LegalAidCertificateForm(props: LegalAidCertificateProps): ReactElement {
    const axios = useAxios()
    const dispatch = useDispatch()
    const styles = useFormStyles()
    const { settings } = useContext<GlobalContextType>(GlobalContext)
    const { t: translate } = useTranslation()
    const validationSchema = useMemo(() => getLegalAidCertificateValidationSchema(translate), [translate])
    const key = useMemo(() => getId(props.selected?.id), [props.certificates.data, props.selected?.id])
    const initialValues = useMemo<LegalAidCertificateDto>(
        () =>
            props.file.loaded && props.parties.loaded && props.selected
                ? mapCertificate(
                      props.certificates.data?.find(certificate => certificate.id === props.selected.id),
                      settings.legalAidCertificate,
                      props.file.data,
                      props.parties.data,
                  )
                : undefined,
        [props.file.loaded, props.parties.loaded, props.certificates.data, props.selected?.id, settings.legalAidCertificate],
    )

    const defaultPartyId = useMemo(() => {
        if (props.legalAidBoards.loaded && props.parties.loaded && settings?.legalAidCertificate) {
            // Take the first one from the list if there is only a single legal aid board party added to
            // this file. The same party can be linked to the file more than once, so compare unique IDs.
            const legalAidBoardPartyIds = props.legalAidBoards.data.map(party => party.id)
            const matches = new Set(props.parties.data.filter(party => legalAidBoardPartyIds.includes(party.id)).map(party => party.id))

            if (matches.size === 1) {
                return matches.values().next().value
            }

            // Take the party ID from the settings if there is one set.
            if (matches.size === 0 && settings.legalAidCertificate.legalAidBoardPartyId) {
                return settings.legalAidCertificate.legalAidBoardPartyId
            }
        }
        return undefined
    }, [props.legalAidBoards.data, props.parties.data, settings])

    useEffect(() => {
        if (initialValues) {
            props.setSelected({
                ...props.selected,
                legalAidBoardPartyId: props.parties.data.find(party => party.pivotId === initialValues.legalAidBoardPivotId)?.id || defaultPartyId,
            })
        }
    }, [initialValues, defaultPartyId])

    const saveCertificate: SaveCertificate = async (dto, setFieldError, options) => {
        dto = { ...dto, contribution: dto.contribution || '0.00' }

        // Reuse the existing relation when the selected legal aid board is already a party of this file.
        if (!dto.legalAidBoardPivotId && props.selected.legalAidBoardPartyId) {
            dto.legalAidBoardPivotId = props.parties.data?.find(party => party.id === props.selected.legalAidBoardPartyId)?.pivotId
        }
        // Create the legal aid board relation first.
        let createdBoardPivot = false
        if (!dto.legalAidBoardPivotId && props.selected.legalAidBoardPartyId) {
            dto.legalAidBoardPivotId = await axios
                .post<string>('/file-party', {
                    fileId: dto.fileId,
                    partyId: props.selected.legalAidBoardPartyId,
                    role: settings.legalAidCertificate.legalAidBoardRole || FilePartyRole.Debtor,
                } as CreateDto)
                .then(response => {
                    createdBoardPivot = true
                    dispatch({ type: FilePartyAction.ResetOverview })
                    return response.data
                })
        }
        if (!dto.legalAidBoardPivotId) {
            setFieldError('legalAidBoardPivotId', translate('legalaidcertificate.board.required'))
            return null
        }

        // Add the certificate reference to the legal aid board party, behind any existing reference.
        const boardParty = props.parties.data?.find(party => party.pivotId === dto.legalAidBoardPivotId)
        if (dto.reference && (createdBoardPivot || boardParty) && !boardParty?.fileReference?.includes(dto.reference)) {
            const fileReference = boardParty?.fileReference
            await axios.put(`/file-party/${dto.legalAidBoardPivotId}`, {
                fileReference: fileReference ? `${fileReference} ${dto.reference}` : dto.reference,
            })
            dispatch({ type: FilePartyAction.ResetOverview })
        }

        const certificateId = options?.certificateId || (!props.selected.isNew ? props.selected.id : undefined)
        const response: AxiosResponse<string> = certificateId
            ? await axios.put(`/registration/legal-aid-certificate/${certificateId}`, dto)
            : await axios.post('/registration/legal-aid-certificate', dto)

        if (options?.finalize) {
            // Set the stored legal aid certificate as the current one.
            props.setSelected({
                ...props.selected,
                id: response.data,
                isNew: false,
                isDirty: false,
            })
            props.reset?.()
        }

        return { certificateId: response.data, legalAidBoardPivotId: dto.legalAidBoardPivotId }
    }

    if (!props.file.loaded || !props.parties.loaded || !settings) {
        return <Loading />
    }

    if (!initialValues) {
        return (
            <div className={styles.alert}>
                <Alert intent="info">Er is geen toevoeging geselecteerd. Kies een bestaande toevoeging uit de lijst of maak een nieuwe aan.</Alert>
            </div>
        )
    }

    return (
        <Formik<LegalAidCertificateDto>
            // Force reload the Formik component when we have selected another certificate, because otherwise the
            // pinned fields won't update correctly.
            key={key}
            validationSchema={validationSchema}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            initialValues={initialValues}
            onSubmit={async (dto: LegalAidCertificateDto, { setSubmitting, setFieldError }) => {
                await saveCertificate(dto, setFieldError, { finalize: true })
                setSubmitting(false)
            }}
        >
            <FormContainer {...props} saveCertificate={saveCertificate} />
        </Formik>
    )
}
